import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface CreatePolicyModalProps {
  onClose: (created?: boolean) => void;
}
const policyContent = `# My Company's Credit Policy

## Overview
This credit policy outlines how our organization evaluates and extends credit to new customers. We built this policy using Credit Pulse's default template as a starting point and customized it to fit our business needs.

## Credit Evaluation Criteria

### New Customer Assessment

**1. Company Verification**
- We verify the applicant's business registration and legal status
- We confirm their business address and contact information
- We validate their business operations and industry classification

**2. Financial Review**
- We analyze available financial statements
- We pull credit reports from major credit bureaus
- We review payment history and check trade references

**3. Risk Assessment**
- We evaluate credit risk scores from our data providers
- We review any relevant business news and public records
- We factor in industry-specific risk considerations

## Credit Limits

We determine initial credit limits based on:
- The applicant's financial strength
- Their payment history with other vendors
- Feedback from trade references
- Industry benchmarks and standards

## Payment Terms

Our standard payment terms are Net 30 days from the invoice date.

## Review Process

Our credit team will review all applications within 2-3 business days. We may request additional documentation during the review process.

---

*Created with Credit Pulse — Automated Credit Decisioning*`;
function renderMarkdown(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let key = 0;
  const flushList = () => {
    if (listItems.length > 0 && listType) {
      if (listType === 'ul') {
        elements.push(<ul key={key++} className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-3" data-id="element-337">
            {listItems}
          </ul>);
      } else {
        elements.push(<ol key={key++} className="list-decimal list-inside space-y-1 text-gray-700 ml-4 mb-3" data-id="element-338">
            {listItems}
          </ol>);
      }
      listItems = [];
      listType = null;
    }
  };
  const formatInline = (str: string) => {
    const parts: React.ReactNode[] = [];
    let remaining = str;
    let partKey = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const italicMatch = remaining.match(/\*(.+?)\*/);
      if (boldMatch && boldMatch.index !== undefined && (!italicMatch || boldMatch.index <= (italicMatch.index ?? Infinity))) {
        if (boldMatch.index > 0) {
          parts.push(<span key={partKey++} data-id="element-339">{remaining.slice(0, boldMatch.index)}</span>);
        }
        parts.push(<strong key={partKey++} className="font-semibold text-gray-900" data-id="element-340">
            {boldMatch[1]}
          </strong>);
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      } else if (italicMatch && italicMatch.index !== undefined) {
        if (italicMatch.index > 0) {
          parts.push(<span key={partKey++} data-id="element-341">
              {remaining.slice(0, italicMatch.index)}
            </span>);
        }
        parts.push(<em key={partKey++} className="italic text-gray-500" data-id="element-342">
            {italicMatch[1]}
          </em>);
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
      } else {
        parts.push(<span key={partKey++} data-id="element-343">{remaining}</span>);
        remaining = '';
      }
    }
    return parts;
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={key++} className="text-base font-semibold text-gray-800 mt-4 mb-2" data-id="element-344">
          {line.slice(4)}
        </h3>);
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={key++} className="text-lg font-bold text-gray-900 mt-5 mb-2" data-id="element-345">
          {line.slice(3)}
        </h2>);
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(<h1 key={key++} className="text-xl font-bold text-gray-900 mb-3" data-id="element-346">
          {line.slice(2)}
        </h1>);
    } else if (line.startsWith('---')) {
      flushList();
      elements.push(<hr key={key++} className="border-gray-200 my-4" data-id="element-347" />);
    } else if (line.match(/^\d+\.\s/)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      const content = line.replace(/^\d+\.\s/, '');
      listItems.push(<li key={key++} className="text-gray-700" data-id="element-348">
          {formatInline(content)}
        </li>);
    } else if (line.match(/^\s*-\s/)) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      const content = line.replace(/^\s*-\s/, '');
      listItems.push(<li key={key++} className="text-gray-700" data-id="element-349">
          {formatInline(content)}
        </li>);
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(<p key={key++} className="text-gray-700 mb-2" data-id="element-350">
          {formatInline(line)}
        </p>);
    }
  }
  flushList();
  return elements;
}
export function CreatePolicyModal({
  onClose
}: CreatePolicyModalProps) {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [displayedContent, setDisplayedContent] = useState('');
  const [policyName] = useState("My Company's Credit Policy");
  const [versionDescription] = useState('Version 1');
  const [useDefaultPolicy, setUseDefaultPolicy] = useState(true);
  useEffect(() => {
    if (step === 2 && isGenerating) {
      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex <= policyContent.length) {
          setDisplayedContent(policyContent.slice(0, currentIndex));
          currentIndex += 2;
        } else {
          setIsGenerating(false);
          clearInterval(interval);
        }
      }, 15);
      return () => clearInterval(interval);
    }
  }, [step, isGenerating]);
  const handleNext = () => {
    if (step === 1) {
      setStep(2);
      setIsGenerating(true);
    }
  };
  const handleCreate = () => {
    onClose(true);
  };
  const renderedContent = useMemo(() => renderMarkdown(displayedContent), [displayedContent]);
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-351">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-352">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-353">
          <div data-id="element-354">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-355">
              Create New Policy
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-356">
              Create a new credit policy from scratch
            </p>
          </div>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600" data-id="element-357">
            <X className="w-6 h-6" data-id="element-358" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-359">
          <div className="flex items-center" data-id="element-360">
            <div className="flex items-center" data-id="element-361">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-white'}`} data-id="element-362">
                1
              </div>
              <span className={`ml-3 font-medium ${step === 1 ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-363">
                Policy Details
              </span>
            </div>
            <div className={`flex-1 h-0.5 mx-4 ${step >= 2 ? 'bg-indigo-600' : 'bg-gray-300'}`} data-id="element-364" />
            <div className="flex items-center" data-id="element-365">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${step === 2 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-white'}`} data-id="element-366">
                2
              </div>
              <span className={`ml-3 font-medium ${step === 2 ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-367">
                Policy Content
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-6" data-id="element-368">
          <AnimatePresence mode="wait" data-id="element-369">
            {step === 1 ? <motion.div key="step1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-370">
                <div data-id="element-371">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-372">
                    Policy Name <span className="text-red-500" data-id="element-373">*</span>
                  </label>
                  <input type="text" value={policyName} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50" data-id="element-374" />
                </div>

                <div data-id="element-375">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-376">
                    Policy Type <span className="text-red-500" data-id="element-377">*</span>
                  </label>
                  <div className="flex gap-6" data-id="element-378">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-379">
                      <input type="radio" name="policyType" checked readOnly className="w-5 h-5 text-indigo-600" data-id="element-380" />
                      <span className="text-gray-900" data-id="element-381">
                        New Customer Policies
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-382">
                      <input type="radio" name="policyType" className="w-5 h-5 text-indigo-600" data-id="element-383" />
                      <span className="text-gray-900" data-id="element-384">
                        Existing Customer Policies
                      </span>
                    </label>
                  </div>
                </div>

                <div data-id="element-385">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-386">
                    Version Description
                  </label>
                  <textarea value={versionDescription} readOnly rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 resize-none" data-id="element-387" />
                  <p className="text-sm text-gray-500 mt-1" data-id="element-388">
                    9/1000 characters
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4" data-id="element-389">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-390">
                    <input type="checkbox" checked={useDefaultPolicy} onChange={e => setUseDefaultPolicy(e.target.checked)} className="w-5 h-5 text-indigo-600 rounded mt-0.5" data-id="element-391" />
                    <div data-id="element-392">
                      <span className="font-medium text-gray-900" data-id="element-393">
                        Start from Credit Pulse's default policy
                      </span>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-394">
                        No template available for this policy type
                      </p>
                    </div>
                  </label>
                </div>
              </motion.div> : <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-395">
                <div className="bg-white rounded-lg p-6 border border-gray-200 min-h-[400px]" data-id="element-396">
                  {renderedContent}
                  {isGenerating && <motion.span animate={{
                opacity: [1, 0]
              }} transition={{
                duration: 0.5,
                repeat: Infinity
              }} className="inline-block w-2 h-5 bg-indigo-600 ml-0.5 align-middle" data-id="element-397" />}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3" data-id="element-398">
          <button onClick={() => onClose(false)} className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium" data-id="element-399">
            Cancel
          </button>
          {step === 1 ? <div className="relative" data-id="element-400">
              <motion.div animate={{
            x: [0, -8, 0]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute right-full mr-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 whitespace-nowrap" data-id="element-401">
                <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-402">
                  Click Next
                </span>
                <ArrowRight className="w-5 h-5 text-gray-900 -ml-1" data-id="element-403" />
              </motion.div>
              <button onClick={handleNext} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium relative z-10" data-id="element-404">
                Next
              </button>
            </div> : <button onClick={handleCreate} disabled={isGenerating} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-405">
              Create
            </button>}
        </div>
      </motion.div>
    </div>;
}